import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard'
import { GaugeChart } from '@/pages/Organization/Analytics/components/GaugeChart'
import { adminApi } from '@/utils/api'
import type { AdminLearningEngagement, AdminDropoutAnalysis } from '@/types/admin'

export default function AdminLearningPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [engagement, setEngagement] = useState<AdminLearningEngagement | null>(null)
  const [dropout, setDropout] = useState<AdminDropoutAnalysis[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        setLoading(true)
        
        // Fetch engagement data
        const engagementRes = await adminApi.learningEngagement()
        const engagementData = (engagementRes as { data?: AdminLearningEngagement }).data
        setEngagement(engagementData || null)
        
        // Fetch dropout analysis
        const dropoutRes = await adminApi.learningDropout()
        const dropoutData = (dropoutRes as { data?: AdminDropoutAnalysis[] }).data
        setDropout(Array.isArray(dropoutData) ? dropoutData : [])
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load learning analytics')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [])

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Learning'
          infoText='Learning engagement, dropout analysis, and reflections.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <StatCard 
          title='Enrollments' 
          value={loading ? '...' : (engagement?.overview?.totalEnrollments?.toLocaleString() || '0')}
          subtitle='Total enrollments across all modules' 
          trend='' 
        />
        <StatCard 
          title='Completions' 
          value={loading ? '...' : (engagement?.overview?.totalCompletions?.toLocaleString() || '0')}
          subtitle='Total completions' 
          trend='' 
        />
        <StatCard 
          title='Dropout Rate' 
          value={loading ? '...' : (
            engagement?.overview?.overallCompletionRate
              ? `${Math.round(100 - engagement.overview.overallCompletionRate)}%`
              : '0%'
          )}
          subtitle='Overall dropout rate' 
          trend='' 
        />
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold text-gray-900'>Module Engagement</h3>
          <button className='px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800'>
            Export
          </button>
        </div>
        {loading && <p className='text-sm text-gray-600'>Loading engagement data...</p>}
        {!loading && (!engagement?.moduleEngagement || engagement.moduleEngagement.length === 0) && (
          <p className='text-sm text-gray-600'>No engagement data available.</p>
        )}
        {!loading && engagement?.moduleEngagement && engagement.moduleEngagement.length > 0 && (
          <div className='grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-800'>
            {engagement.moduleEngagement.map((module) => (
              <div key={module.moduleId} className='p-3 rounded-lg bg-gray-50'>
                <p className='text-xs text-gray-500'>{module.moduleTitle || `Module ${module.moduleId}`}</p>
                <p className='text-lg font-semibold text-gray-900'>
                  Completion {Math.round(module.completionRate || 0)}%
                </p>
                <p className='text-xs text-gray-600'>Dropout {Math.round(module.dropoutRate || 0)}%</p>
                <p className='text-xs text-gray-500 mt-1'>
                  {module.completions?.toLocaleString() || 0} / {module.enrollments?.toLocaleString() || 0}
                </p>
              </div>
            ))}
          </div>
        )}
        <p className='text-xs text-gray-500'>
          Module-level insights showing enrollments, completions, and dropout rates.
        </p>
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
        <h3 className='text-lg font-semibold text-gray-900 mb-4'>Dropout Analysis</h3>
        {loading && <p className='text-sm text-gray-600'>Loading dropout data...</p>}
        {!loading && dropout.length > 0 && (
          <>
            <div className='mb-4'>
              <GaugeChart 
                value={Math.round(dropout.reduce((sum, d) => sum + (d.averageDropoutPosition || 0), 0) / dropout.length || 76)} 
                label='Avg Completion Point' 
              />
            </div>
            <p className='text-sm text-gray-600 mb-4'>
              Highlight where users abandon content to guide content improvements.
            </p>
            <div className='space-y-2'>
              <h4 className='text-sm font-semibold text-gray-800'>Dropout by Module:</h4>
              {dropout.map((mod) => (
                <div key={mod.moduleId} className='flex justify-between items-center text-sm p-2 rounded bg-gray-50'>
                  <span className='text-gray-700'>{mod.moduleTitle || mod.moduleId}</span>
                  <span className='text-gray-600'>
                    {Math.round(mod.dropoutRate || 0)}% dropout at {Math.round(mod.averageDropoutPosition || 0)}%
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
        {!loading && dropout.length === 0 && (
          <p className='text-sm text-gray-600'>No dropout data available.</p>
        )}
      </div>
      {error && <p className='text-sm text-red-600'>{error}</p>}
    </div>
  )
}
